import * as S from './styles';
import PanelOptionMixin from '../mixin';
import ChartColumnIcon from '@svgIcons/dashboard/widget_list_icons/column.svg';
import Switch from '@components/Common/Switch';
import withHMR from '@/utils/withHMR';
import { CHART_TYPES } from '@/utils/consts';

const { BAR, HORIZONTAL_BAR } = CHART_TYPES;

const MAX_VALUE = 1_000_000_000_000_000;

const AxesConfigOption = {
    name: 'AxesConfigOption',
    mixins: [PanelOptionMixin],
    props: {
        axesConfig: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            minValue: null,
            maxValue: null,
            localAutoScale: null,
        };
    },
    computed: {
        showOption: R.compose(
            R.includes(R.__, [BAR, HORIZONTAL_BAR]),
            R.pathOr('', ['graphOptions', 'selectedGraphType'])
        ),
        isYAxis: R.compose(
            R.includes(R.__, [BAR]),
            R.pathOr('', ['graphOptions', 'selectedGraphType'])
        ),
        axisKey() {
            return this.isYAxis ? 'yAxis' : 'xAxis';
        },
        axisConfig() {
            return this.axesConfig?.[this.axisKey] ?? {};
        },
        autoScaleEnabled() {
            if (this.localAutoScale !== null) {
                return this.localAutoScale;
            }
            return this.axisConfig?.autoScaleEnabled ?? true;
        },
        hasMinMaxError() {
            const { min, max } = this.axisConfig;
            if (R.isNil(min) || R.isNil(max)) return false;
            return Number(min) > Number(max);
        },
    },
    watch: {
        minValue(newValue) {
            this.$emit('update', {
                [this.axisKey]: {
                    min: newValue,
                },
            });
        },
        maxValue(newValue) {
            this.$emit('update', {
                [this.axisKey]: {
                    max: newValue,
                },
            });
        },
        'axisConfig.autoScaleEnabled'(newValue) {
            this.localAutoScale = newValue ?? true;
        },
    },
    methods: {
        handleAutoScalingToggle() {
            const currentValue = this.autoScaleEnabled;
            const newValue = !currentValue;

            this.localAutoScale = newValue;

            this.$emit('update', {
                [this.axisKey]: {
                    autoScaleEnabled: newValue,
                },
            });
        },

        checkIsValidInput(value) {
            if (!/^-?\d*\.?\d*$/.test(value)) return false;

            const numValue = Number(value);
            return (
                !isNaN(numValue) &&
                numValue >= -MAX_VALUE &&
                numValue <= MAX_VALUE
            );
        },

        handleValueChange(e, type) {
            const value = e.target.value;

            if (value === '') {
                if (type === 'min') {
                    this.minValue = null;
                } else if (type === 'max') {
                    this.maxValue = null;
                }
                return;
            }

            if (value === '-') {
                return;
            }

            if (!this.checkIsValidInput(value)) {
                e.target.value = type === 'min' ? this.minValue : this.maxValue;
                return;
            }

            const valueToSet = Number(value);

            if (type === 'min') {
                this.minValue = valueToSet;
            } else if (type === 'max') {
                this.maxValue = valueToSet;
            }
        },

        handleReset() {
            this.minValue = null;
            this.maxValue = null;
            this.localAutoScale = true;

            this.$emit('update', {
                [this.axisKey]: {
                    autoScaleEnabled: true,
                    min: null,
                    max: null,
                },
            });
        },
    },
    created() {
        this.minValue = this.axisConfig?.min ?? null;
        this.maxValue = this.axisConfig?.max ?? null;
        this.localAutoScale = this.axisConfig?.autoScaleEnabled ?? true;
    },
    render() {
        const { autoScaleEnabled } = this;

        if (!this.showOption) {
            return null;
        }

        return (
            <S.ComponentWrapper>
                <S.Header>
                    <S.Title>
                        <ChartColumnIcon width={16} height={16} v-view-box />
                        <h1>
                            {this.$t('NEW_DASHBOARD.axes_config_option_title', {
                                axis: this.isYAxis ? 'Y' : 'X',
                            })}
                        </h1>
                    </S.Title>
                    <S.ResetButton onClick={this.handleReset}>
                        {this.$t('COMMON.reset')}
                    </S.ResetButton>
                </S.Header>
                <S.Content>
                    <S.Row>
                        <S.RowTitle>
                            {this.$t(
                                'NEW_DASHBOARD.axes_config_option_auto_scaling',
                                {
                                    axis: this.isYAxis ? 'Y' : 'X',
                                }
                            )}
                        </S.RowTitle>
                        <Switch
                            value={autoScaleEnabled}
                            type="big"
                            onClick={this.handleAutoScalingToggle}
                        />
                    </S.Row>
                    {!autoScaleEnabled && (
                        <S.Row>
                            <S.RowTitle>
                                {this.$t(
                                    'NEW_DASHBOARD.axes_config_option_min_value'
                                )}
                            </S.RowTitle>
                            <S.InputWrapper>
                                <input
                                    type="text"
                                    placeholder={this.$t(
                                        'NEW_DASHBOARD.axes_config_option_min_value_placeholder'
                                    )}
                                    value={this.minValue}
                                    onInput={(e) => {
                                        this.handleValueChange(e, 'min');
                                    }}
                                />
                            </S.InputWrapper>
                        </S.Row>
                    )}
                    {!autoScaleEnabled && (
                        <S.Row>
                            <S.RowTitle>
                                {this.$t(
                                    'NEW_DASHBOARD.axes_config_option_max_value'
                                )}
                            </S.RowTitle>
                            <S.InputWrapper>
                                <input
                                    type="text"
                                    placeholder={this.$t(
                                        'NEW_DASHBOARD.axes_config_option_max_value_placeholder'
                                    )}
                                    value={this.maxValue}
                                    onInput={(e) => {
                                        this.handleValueChange(e, 'max');
                                    }}
                                />
                                {this.hasMinMaxError && (
                                    <S.ErrorText>
                                        {this.$t(
                                            'NEW_DASHBOARD.axes_config_option_min_max_error'
                                        )}
                                    </S.ErrorText>
                                )}
                            </S.InputWrapper>
                        </S.Row>
                    )}
                </S.Content>
            </S.ComponentWrapper>
        );
    },
};

export default withHMR(module, AxesConfigOption, AxesConfigOption.name);
